import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { setTheme } from '@/store/appSlice';
import { Tooltip } from '@/components/ui/Tooltip';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { useWorkspaceTitle } from '@/hooks/useWorkspaceTitle';
import { CompanySwitcher } from '@/components/layout/CompanySwitcher';
import { PosStorePicker } from '@/components/pos/PosStorePicker';
import { PosStoreSelector } from '@/components/pos/PosStoreSelector';
import { PosCounterPicker } from '@/components/pos/PosCounterPicker';
import { PosHardwareProvider } from '@/pages/pos/PosHardwareContext';
import { PosSessionProvider } from '@/hooks/usePosSession';
import { KeyboardShortcutProvider } from '@/core/keyboard';
import type { ShortcutScope } from '@/core/keyboard/types';

const TERMINAL_NAV = [
  { path: '/pos', label: 'Billing' },
  { path: '/pos/held', label: 'Held' },
  { path: '/pos/shift', label: 'Shift' },
  { path: '/pos/returns', label: 'Returns' },
  { path: '/pos/analytics', label: 'Analytics' },
  { path: '/pos/setup', label: 'Setup' },
  { path: '/pos/shortcuts', label: 'Guide' },
] as const;

function resolvePosShortcutScope(pathname: string): ShortcutScope {
  if (pathname === '/pos/held' || pathname.startsWith('/pos/held/')) return 'pos-held';
  return 'pos';
}

function PosLayoutShell() {
  useWorkspaceTitle();
  const posModuleLabel = getModuleEntryByErpModule('POS')?.label ?? 'Point of Sale';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedStore = useAppSelector((s) => s.app.selectedStore);
  const companyScopeVersion = useAppSelector((s) => s.app.companyScopeVersion);
  const user = useAppSelector((s) => s.auth.user);
  const theme = useAppSelector((s) => s.app.theme);
  const location = useLocation();
  const shortcutScope = resolvePosShortcutScope(location.pathname);

  return (
    <KeyboardShortcutProvider scope={shortcutScope}>
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 dark:border-slate-700 dark:bg-slate-950">
        <div className="flex min-w-0 items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(user?.masterAdmin ? '/master' : '/pos/dashboard')}
            className="flex shrink-0 items-center gap-1 text-sm text-slate-900 hover:text-primary-600 dark:text-white dark:hover:text-primary-400"
          >
            <ArrowBackIcon fontSize="small" />
            Dashboard
          </button>
          <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {posModuleLabel} terminal
          </span>
          <CompanySwitcher className="min-w-[8rem] max-w-[12rem] sm:min-w-[10rem] sm:max-w-[15rem]" />
          <PosStoreSelector className="min-w-[8rem] max-w-[12rem] sm:min-w-[10rem] sm:max-w-[15rem]" />
        </div>
        <nav className="hidden gap-1 text-sm md:flex">
          {TERMINAL_NAV.map((item) => (
            <button
              key={item.path}
              type="button"
              className={`rounded-lg px-2.5 py-1.5 transition ${
                location.pathname === item.path
                  ? 'bg-primary-50 font-semibold text-primary-700 dark:bg-primary-950/50 dark:text-primary-300'
                  : 'text-slate-700 hover:bg-slate-100 hover:text-primary-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-primary-300'
              }`}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div className="flex shrink-0 items-center gap-3">
          {selectedStore && <PosCounterPicker />}
          <Tooltip content="Toggle theme">
            <button
              type="button"
              className="workspace-header-icon-btn"
              onClick={() => dispatch(setTheme(theme === 'dark' ? 'light' : 'dark'))}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </button>
          </Tooltip>
          <span className="hidden text-sm text-slate-500 dark:text-slate-400 sm:inline">
            {user?.firstName} {user?.lastName}
          </span>
        </div>
      </header>
      <div className="flex-1 overflow-hidden">
        {selectedStore ? (
          <PosHardwareProvider key={`${companyScopeVersion}-${selectedStore.id}`}>
            <Outlet key={companyScopeVersion} />
          </PosHardwareProvider>
        ) : (
          <PosStorePicker />
        )}
      </div>
    </KeyboardShortcutProvider>
  );
}

export function PosLayout() {
  const selectedStore = useAppSelector((s) => s.app.selectedStore);
  const companyScopeVersion = useAppSelector((s) => s.app.companyScopeVersion);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-100 text-slate-900 dark:bg-slate-900 dark:text-slate-100">
      {selectedStore ? (
        <PosSessionProvider key={`${companyScopeVersion}-${selectedStore.id}`} storeId={selectedStore.id}>
          <PosLayoutShell />
        </PosSessionProvider>
      ) : (
        <PosLayoutShell />
      )}
    </div>
  );
}
